import * as THREE from 'three';
import { DESKTOP, ROOM } from '../config';

/**
 * 데스크탑 1인칭 컨트롤: pointer-lock으로 시선을 돌리고 WASD로 이동한다.
 * VR 프레젠팅 중에는 three가 카메라를 제어하므로 update()가 즉시 빠져나간다.
 */
export class DesktopControls {
  private yaw = 0;
  private pitch = 0;
  private locked = false;
  private readonly keys: Record<string, boolean> = {};

  private readonly boundX: number;
  private readonly boundZ: number;

  // 매 프레임 할당을 피하려 재사용하는 벡터 (Quest 72fps 예산 배려).
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly step = new THREE.Vector3();

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly dolly: THREE.Object3D,
    private readonly enterOverlay: HTMLElement,
  ) {
    this.boundX = ROOM.w / 2 - DESKTOP.wallMargin;
    this.boundZ = ROOM.d / 2 - DESKTOP.wallMargin;
    this.bindEvents();
  }

  private bindEvents(): void {
    this.enterOverlay.addEventListener('click', () => {
      this.renderer.domElement.requestPointerLock?.();
    });

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.renderer.domElement;
      this.enterOverlay.style.opacity = this.locked ? '0' : '1';
      this.enterOverlay.style.pointerEvents = this.locked ? 'none' : 'auto';
    });

    document.addEventListener('mousemove', (e) => {
      if (!this.locked) return;
      this.yaw -= e.movementX * DESKTOP.sensitivity;
      this.pitch = THREE.MathUtils.clamp(
        this.pitch - e.movementY * DESKTOP.sensitivity,
        -DESKTOP.pitchClamp,
        DESKTOP.pitchClamp,
      );
    });

    window.addEventListener('keydown', (e) => {
      this.keys[e.code] = true;
    });
    window.addEventListener('keyup', (e) => {
      this.keys[e.code] = false;
    });
  }

  update(dt: number): void {
    if (this.renderer.xr.isPresenting) return;

    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;

    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this.step.set(0, 0, 0);

    if (this.keys.KeyW) this.step.add(this.forward);
    if (this.keys.KeyS) this.step.sub(this.forward);
    if (this.keys.KeyD) this.step.add(this.right);
    if (this.keys.KeyA) this.step.sub(this.right);

    if (this.step.lengthSq() > 0) {
      this.step.normalize().multiplyScalar(DESKTOP.speed * dt);
      this.dolly.position.x = THREE.MathUtils.clamp(
        this.dolly.position.x + this.step.x,
        -this.boundX,
        this.boundX,
      );
      this.dolly.position.z = THREE.MathUtils.clamp(
        this.dolly.position.z + this.step.z,
        -this.boundZ,
        this.boundZ,
      );
    }
  }
}
